import { actions } from "astro:actions";
import type { IPinfo } from "node-ipinfo";
import type React from "react";
import { z } from "zod";

import { useGameState } from "~/hooks/game";
import { useGameSettings } from "~/hooks/settings";
import { useSearchParam } from "~/hooks/url";
import type { Locale } from "~/i18n";
import { trackEvent } from "~/utils/analytics";
import { DEFAULT_TIME_LIMIT, TIME_LIMIT_MAX } from "~/utils/const";
import type { FragmentType } from "~/utils/fragment";

import { GAME_MODES, getPoints } from "../Game";
import { GameProvider } from "../GameProvider";
import { GameSettingsSchema } from "../GameSettings";
import { IPinfoContext } from "../IPinfoContext";
import { LocaleContext } from "../LocaleContext";
import type { PackGame_PackFragment } from "./PackGame_PackFragment";
import { PackPreGame } from "./PackPreGame";

const PackGameSchema = GameSettingsSchema.extend({
  time: z.coerce
    .number()
    .nonnegative()
    .max(TIME_LIMIT_MAX)
    .catch(DEFAULT_TIME_LIMIT),
  mode: z.enum(GAME_MODES).catch("classic"),
});

export const PackGame: React.FC<{
  pack: FragmentType<"Pack", { select: typeof PackGame_PackFragment }>;
  children: React.ReactElement;
  locale: Locale;
  ipInfo: IPinfo | null;
  searchParams: Record<string, string>;
}> = ({ pack, children, locale, searchParams, ipInfo }) => {
  const { time, mode, first_letter, typing_feedback, hint, time_rewards } =
    PackGameSchema.parse(searchParams);

  const [game, setGame] = useGameState({
    mode,
    timeLimit: time,
    time: 0,
    words: [],
    answers: [],
    settings: {
      isFirstLetterShown: first_letter,
      isTypingFeedbackShown: typing_feedback,
      isHintEnabled: hint,
      isTimeRewardEnabled: time_rewards,
      isPercentShown: false,
      strictness: {
        cap: false,
        pun: false,
        acc: false,
      },
    },
  });

  useSearchParam("mode", game.mode);
  useSearchParam("time", game.timeLimit.toString());
  useGameSettings(game.settings);

  return (
    <IPinfoContext value={ipInfo}>
      <LocaleContext value={locale}>
        <GameProvider
          game={game}
          setGame={setGame}
          header={children}
          onCompleted={(gameState) => {
            trackEvent("pack_completed");
            actions.game.saveGame({
              time: gameState.time,
              points: getPoints(gameState.answers),
              packId: pack.id,
              answers: [],
            });
          }}
        >
          <PackPreGame pack={pack} />
        </GameProvider>
      </LocaleContext>
    </IPinfoContext>
  );
};
